import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Check, ShieldCheck, Users, BookOpen, Sparkles } from "lucide-react";

import PageMeta from "../components/layout/PageMeta";
import FinalCTA from "../components/sections/FinalCTA";
import MiniContactCard from "../components/sections/MiniContactCard";
import { PRODUCTS, getProduct } from "../data/products";

export default function ProductDetail() {
    const { slug } = useParams();
    const product = getProduct(slug);

    if (!product) return <Navigate to="/products" replace />;

    const others = PRODUCTS.filter((p) => p.slug !== product.slug);

    return (
        <div className="pqx-page-enter" data-testid={`product-detail-${product.slug}`}>
            <PageMeta
                title={`${product.name} — ${product.tagline}`}
                description={product.short}
            />

            {/* Hero */}
            <section className="relative pt-28 md:pt-36 pb-12 md:pb-16 overflow-hidden pqx-dark-panel">
                <div className="absolute inset-0 pqx-grid-bg-dark opacity-50" />
                <div
                    className="absolute right-[-10%] top-[20%] w-[420px] h-[420px] rounded-full pointer-events-none blur-3xl opacity-50"
                    style={{ background: "radial-gradient(circle, rgba(255,64,64,0.20), transparent 60%)" }}
                />
                <div className="pqx-container relative z-10 max-w-5xl">
                    <Link
                        to="/products"
                        data-testid="product-back"
                        className="inline-flex items-center gap-2 text-sm text-white/65 hover:text-white pqx-reveal"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        All product accelerators
                    </Link>
                    <span className="pqx-eyebrow !text-[#FF4040] mt-6 inline-flex pqx-reveal pqx-delay-1">
                        Product accelerator · {product.tagline}
                    </span>
                    <h1 className="pqx-h1 mt-5 text-white pqx-reveal pqx-delay-2">
                        {product.name}
                    </h1>
                    <p className="mt-6 text-white/75 text-base md:text-lg lg:text-xl leading-relaxed max-w-3xl pqx-reveal pqx-delay-3">
                        {product.short}
                    </p>
                    <div className="mt-10 grid sm:grid-cols-3 gap-4 pqx-reveal pqx-delay-4">
                        {product.outcomes.map((o) => (
                            <div key={o.label} className="pqx-glass-dark rounded-xl p-4">
                                <p className="pqx-display text-2xl text-[#FF4040]">{o.value}</p>
                                <p className="text-xs uppercase tracking-[0.16em] text-white/55 mt-1">
                                    {o.label}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-[#FAFAF8] py-20" data-testid="product-body">
                <div className="pqx-container grid lg:grid-cols-[1.5fr_1fr] gap-10">
                    <div className="space-y-12">
                        {/* Problem */}
                        <div>
                            <span className="pqx-eyebrow">Problem solved</span>
                            <h2 className="pqx-display mt-4 text-2xl md:text-4xl text-[#303030]">
                                The reason {product.name} exists.
                            </h2>
                            <p className="mt-5 text-[16px] text-[#303030]/80 leading-relaxed max-w-3xl">
                                {product.problem}
                            </p>
                        </div>

                        {/* Capabilities */}
                        <div>
                            <span className="pqx-eyebrow">Key capabilities</span>
                            <h2 className="pqx-display mt-4 text-2xl md:text-4xl text-[#303030]">
                                What's inside.
                            </h2>
                            <div className="mt-6 grid sm:grid-cols-2 gap-3">
                                {product.capabilities.map((c, i) => (
                                    <div
                                        key={c}
                                        data-testid={`product-capability-${i}`}
                                        className="flex items-start gap-3 p-4 rounded-xl bg-white border border-[#303030]/8"
                                    >
                                        <span className="mt-0.5 w-5 h-5 rounded-full bg-[#FF4040]/12 text-[#FF4040] grid place-items-center shrink-0">
                                            <Check className="w-3 h-3" />
                                        </span>
                                        <p className="text-sm text-[#303030]/85 leading-relaxed">{c}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Module highlights */}
                        <div>
                            <span className="pqx-eyebrow">Module highlights</span>
                            <h2 className="pqx-display mt-4 text-2xl md:text-4xl text-[#303030]">
                                Designed for the way enterprises buy.
                            </h2>
                            <div className="mt-6 grid sm:grid-cols-3 gap-4">
                                {product.moduleHighlights.map((m, i) => (
                                    <div
                                        key={m.title}
                                        className="pqx-card-elevated !p-5"
                                        data-testid={`product-highlight-${i}`}
                                    >
                                        <span className="w-9 h-9 grid place-items-center rounded-xl bg-[#303030]/5 text-[#303030]">
                                            <Sparkles className="w-4 h-4" />
                                        </span>
                                        <h3 className="pqx-display text-base text-[#303030] mt-4">
                                            {m.title}
                                        </h3>
                                        <p className="mt-2 text-xs text-[#303030]/65 leading-relaxed">
                                            {m.copy}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Customer story */}
                        <div
                            className="rounded-3xl bg-[#191919] text-[#FAFAF8] p-8 md:p-10 relative overflow-hidden"
                            data-testid="product-story"
                        >
                            <div className="absolute inset-0 pqx-grid-bg-dark opacity-40" />
                            <div className="relative">
                                <span className="pqx-eyebrow !text-[#FF4040] inline-flex items-center gap-2">
                                    <BookOpen className="w-3.5 h-3.5" />
                                    Customer story
                                </span>
                                <h2 className="pqx-display text-2xl md:text-3xl mt-5">
                                    {product.story.title}
                                </h2>
                                <p className="mt-5 text-[15px] text-white/80 leading-relaxed max-w-3xl">
                                    {product.story.copy}
                                </p>
                                <p className="mt-7 text-xs text-white/45">
                                    Anonymised at the client's request.
                                </p>
                            </div>
                        </div>

                        {/* Ideal users */}
                        <div className="pqx-card-elevated !p-8">
                            <span className="pqx-eyebrow inline-flex items-center gap-2">
                                <Users className="w-3.5 h-3.5" />
                                Ideal users
                            </span>
                            <h2 className="pqx-display text-2xl text-[#303030] mt-4">
                                Built for these teams.
                            </h2>
                            <div className="mt-5 flex flex-wrap gap-2">
                                {product.users.map((u) => (
                                    <span
                                        key={u}
                                        className="text-[13px] px-3 py-1.5 rounded-full bg-[#303030]/5 text-[#303030]/80 font-medium"
                                    >
                                        {u}
                                    </span>
                                ))}
                            </div>
                            <div className="mt-7 flex items-center gap-3 text-sm text-[#303030]/65 border-t border-[#303030]/8 pt-5">
                                <ShieldCheck className="w-4 h-4 text-[#FF4040]" />
                                Enterprise security, role-based access and audit trails.
                            </div>
                        </div>

                        {/* Other products */}
                        <div>
                            <span className="pqx-eyebrow">Other accelerators</span>
                            <h2 className="pqx-display mt-4 text-2xl text-[#303030]">
                                Compose with the rest of the IQX family.
                            </h2>
                            <div className="mt-6 grid sm:grid-cols-3 gap-4">
                                {others.map((p) => (
                                    <Link
                                        key={p.slug}
                                        to={`/products/${p.slug}`}
                                        data-testid={`product-other-${p.slug}`}
                                        className="pqx-card-elevated !p-5 group"
                                    >
                                        <p className="pqx-display text-base text-[#303030] group-hover:text-[#FF4040] transition-colors">
                                            {p.name}
                                        </p>
                                        <p className="text-xs uppercase tracking-[0.16em] text-[#FF4040] font-semibold mt-1">
                                            {p.tagline}
                                        </p>
                                        <p className="mt-3 text-xs text-[#303030]/65 leading-relaxed">
                                            {p.short}
                                        </p>
                                        <span className="inline-flex items-center gap-1.5 mt-4 text-xs font-semibold text-[#303030] group-hover:text-[#FF4040]">
                                            Open product
                                            <ArrowUpRight className="w-3.5 h-3.5" />
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <aside className="lg:pl-2">
                        <MiniContactCard
                            serviceInterest={product.name}
                            eyebrow={`Inside ${product.name}`}
                            heading={`Book a ${product.name} walkthrough.`}
                            description="We'll show the accelerator with sample data shaped like yours — no commitment."
                            testidPrefix={`product-contact-${product.slug}`}
                        />
                    </aside>
                </div>
            </section>

            <FinalCTA
                title={`See ${product.name} in your context.`}
                description="Book a guided walkthrough — we'll tailor the demo to your data, team and operating model."
                primary={{ to: "/contact", label: "Request a walkthrough" }}
                secondary={{ to: "/products", label: "All accelerators" }}
            />
        </div>
    );
}
